/**
 * MediaLightbox - Componente de preview fullscreen para imagens e vídeos
 * Versão standalone para o widget do WebChat (100% CSS inline, sem dependências)
 * Suporta navegação de galeria, download, abrir em nova aba e controles por teclado
 */

import React, { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { getStorageUrl } from '../utils/storageUrl';

// Interface para item de mídia
interface MediaItem {
  url: string;
  type: 'image' | 'video';
  filename?: string;
}

// Props do componente
interface MediaLightboxProps {
  open: boolean;
  onClose: () => void;
  mediaItems: MediaItem[];
  initialIndex?: number;
  primaryColor?: string;
}

// Ícones SVG inline
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7,10 12,15 17,10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15,3 21,3 21,9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15,18 9,12 15,6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9,6 15,12 9,18" />
  </svg>
);

export const MediaLightbox: React.FC<MediaLightboxProps> = ({
  open,
  onClose,
  mediaItems,
  initialIndex = 0,
  primaryColor = '#3b82f6',
}) => {
  // Estado do índice atual na galeria
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isClosing, setIsClosing] = useState(false);

  // Reseta o índice quando o lightbox abre
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setIsClosing(false);
    }
  }, [open, initialIndex]);

  // Navegação
  const goToPrevious = useCallback(() => {
    if (mediaItems.length > 1) {
      setCurrentIndex(prev => (prev - 1 + mediaItems.length) % mediaItems.length);
    }
  }, [mediaItems.length]);

  const goToNext = useCallback(() => {
    if (mediaItems.length > 1) {
      setCurrentIndex(prev => (prev + 1) % mediaItems.length);
    }
  }, [mediaItems.length]);

  // Controles de teclado
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!open) return;
    
    switch (e.key) {
      case 'Escape':
        handleClose();
        break;
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
    }
  }, [open, goToPrevious, goToNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Fecha com animação
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  // Download da mídia
  const handleDownload = useCallback(async () => {
    const item = mediaItems[currentIndex];
    if (!item) return;
    
    // Converte URL relativa para absoluta
    const absoluteUrl = getStorageUrl(item.url);

    try {
      const response = await fetch(absoluteUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename || `media-${currentIndex + 1}.${item.type === 'video' ? 'mp4' : 'jpg'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback: abre em nova aba se download falhar
      window.open(absoluteUrl, '_blank');
    }
  }, [mediaItems, currentIndex]);

  // Abre em nova aba
  const handleOpenInNewTab = useCallback(() => {
    const item = mediaItems[currentIndex];
    if (item) {
      window.open(getStorageUrl(item.url), '_blank');
    }
  }, [mediaItems, currentIndex]);

  // Não renderiza se fechado ou sem mídia
  if (!open || mediaItems.length === 0) return null;

  const currentItem = mediaItems[currentIndex];
  const isGalleryMode = mediaItems.length > 1;

  // Estilos
  const overlayStyles: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    animation: isClosing ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out',
  };

  const headerStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  };

  const headerLeftStyles: React.CSSProperties = {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 500,
  };

  const headerActionsStyles: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
  };

  const actionButtonStyles: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    color: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  };

  const contentStyles: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  };

  const navButtonStyles: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    color: '#ffffff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    zIndex: 10,
  };

  const mediaContainerStyles: React.CSSProperties = {
    maxWidth: '90vw',
    maxHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const imageStyles: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '80vh',
    objectFit: 'contain',
    borderRadius: '4px',
    animation: 'scaleIn 0.2s ease-out',
  };

  const videoStyles: React.CSSProperties = {
    maxWidth: '100%',
    maxHeight: '80vh',
    borderRadius: '4px',
    outline: 'none',
  };

  const indicatorContainerStyles: React.CSSProperties = {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
  };

  const indicatorStyles = (isActive: boolean): React.CSSProperties => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: isActive ? primaryColor : 'rgba(255, 255, 255, 0.4)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    border: 'none',
    padding: 0,
  });

  // Usa createPortal para renderizar diretamente no document.body
  // Isso permite que o lightbox escape do contexto do widget e abra em fullscreen real
  return createPortal(
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .lightbox-action-btn:hover { background: rgba(255, 255, 255, 0.2) !important; }
        .lightbox-nav-btn:hover { background: rgba(255, 255, 255, 0.25) !important; }
        .lightbox-indicator:hover { background: rgba(255, 255, 255, 0.6) !important; }
      `}</style>

      <div 
        style={overlayStyles} 
        onClick={(e) => e.target === e.currentTarget && handleClose()}
        role="dialog"
        aria-modal="true"
        aria-label="Visualizador de mídia"
      >
        {/* Header com ações */}
        <div style={headerStyles}>
          <div style={headerLeftStyles}>
            {isGalleryMode && `${currentIndex + 1} de ${mediaItems.length}`}
          </div>
          <div style={headerActionsStyles}>
            <button 
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              style={actionButtonStyles}
              className="lightbox-action-btn"
              aria-label="Baixar"
              title="Baixar"
            >
              <DownloadIcon />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); handleOpenInNewTab(); }}
              style={actionButtonStyles}
              className="lightbox-action-btn"
              aria-label="Abrir em nova aba"
              title="Abrir em nova aba"
            >
              <ExternalLinkIcon />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
              style={actionButtonStyles}
              className="lightbox-action-btn"
              aria-label="Fechar"
              title="Fechar"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Conteúdo principal */}
        <div style={contentStyles}>
          {/* Botão anterior */}
          {isGalleryMode && (
            <button 
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              style={{ ...navButtonStyles, left: '16px' }}
              className="lightbox-nav-btn"
              aria-label="Mídia anterior"
            >
              <ChevronLeftIcon />
            </button>
          )}

          {/* Mídia */}
          <div style={mediaContainerStyles}>
            {currentItem.type === 'video' ? (
              <video 
                key={currentItem.url}
                src={getStorageUrl(currentItem.url)} 
                controls 
                autoPlay
                style={videoStyles}
              />
            ) : (
              <img 
                key={currentItem.url}
                src={getStorageUrl(currentItem.url)} 
                alt={currentItem.filename || 'Imagem'} 
                style={imageStyles}
              />
            )}
          </div>

          {/* Botão próximo */}
          {isGalleryMode && (
            <button 
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              style={{ ...navButtonStyles, right: '16px' }}
              className="lightbox-nav-btn"
              aria-label="Próxima mídia"
            >
              <ChevronRightIcon />
            </button>
          )}
        </div>

        {/* Indicadores de posição */}
        {isGalleryMode && (
          <div style={indicatorContainerStyles}>
            {mediaItems.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                style={indicatorStyles(index === currentIndex)}
                className="lightbox-indicator"
                aria-label={`Ir para mídia ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </>,
    document.body
  );
};
